import React from 'react'
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import { usePermission } from 'admin/hocs'
import { UnderlineNavigationTabs } from 'uiComponents/navigation/underlineNavigationTabs'
import InSectionTabsSlot from 'uiComponents/navigation/sectionTabsSlot'

interface EngageSection {
    name: string
    path: string
    activeKey: string
    shouldShow: boolean
}

export function useEngageSections(): EngageSection[] {
    const { accountSlug } = useParams<BaseRouteParams>()
    const location = useLocation()
    const { hasPermission } = usePermission()

    const engage = `/account/${accountSlug}/engage`
    const canViewTools = hasPermission('view_wonderbar', accountSlug)
    const isPartnerAdmin = hasPermission('partner_admin', accountSlug)

    const onToolsPage = location.pathname.includes('/engage/tools/home')
    const toolSearch = onToolsPage ? location.search : ''
    const toolTab = (name: string, segment: string): EngageSection => ({
        name,
        path: `${engage}/tools/home/${segment}${toolSearch}`,
        activeKey: `/engage/tools/home/${segment}`,
        shouldShow: canViewTools,
    })

    return [
        toolTab('Checkouts', 'checkout'),
        toolTab('Trigger Buttons', 'trigger_button'),
        toolTab('Pop Ups', 'pop_up'),
        toolTab('Wonderbars', 'wonderbar'),
        {
            name: 'Copy Editor',
            path: `${engage}/copy_editor/home/`,
            activeKey: '/engage/copy_editor',
            shouldShow: isPartnerAdmin,
        },
        {
            name: 'Email Templates',
            path: `${engage}/email_templates/home/`,
            activeKey: '/engage/email_templates',
            shouldShow: isPartnerAdmin,
        },
        {
            name: 'Ticket Templates',
            path: `${engage}/ticket_templates/`,
            activeKey: '/engage/ticket_templates',
            shouldShow: isPartnerAdmin,
        },
        {
            name: 'Gift Card Templates',
            path: `${engage}/gift_card_templates/`,
            activeKey: '/engage/gift_card_templates',
            shouldShow: isPartnerAdmin,
        },
    ].filter((section) => section.shouldShow)
}

export function EngageIndexRedirect() {
    const { accountSlug } = useParams<BaseRouteParams>()
    const sections = useEngageSections()
    return <Redirect to={sections[0]?.path ?? `/account/${accountSlug}/engage/tools/home/`} />
}

function EngageSectionTabs() {
    const location = useLocation()
    const history = useHistory()
    const sections = useEngageSections()

    const tabs = sections.map((section) => ({
        name: section.name,
        current: location.pathname.includes(section.activeKey),
        href: section.path,
        onClick: () => history.push(section.path),
    }))

    if (tabs.length < 2) {
        return null
    }

    return (
        <InSectionTabsSlot>
            <UnderlineNavigationTabs tabs={tabs} />
        </InSectionTabsSlot>
    )
}

export default EngageSectionTabs
